export type TicketState =
  | 'Backlog'
  | 'Refining'
  | 'Implementing'
  | 'GitLab CI'
  | 'AI Review'
  | 'Human Review'
  | 'Merging'
  | 'Done'
  | 'Waiting'
  | 'Cancelled';

/** `test` is legacy: no new run can carry it, but recorded runs from the passthrough era do. */
type Stage = 'refine' | 'plan' | 'implement' | 'test' | 'review';
export type JobStage = Stage;
export type Vendor = 'claude' | 'codex' | 'cursor' | 'kimi';

export interface StateStyle {
  /** The canonical hue, as seeded on the board. Used for dots, edges and bars. */
  line: string;
  /** Readable tint of the same hue on this ground. */
  text: string;
  wash: string;
  edge: string;
}

/**
 * The ten pipeline states.
 *
 * Order is pipeline order, not alphabetical — the board reads this array directly, so the
 * columns cannot drift out of sequence.
 */
export const STATE_ORDER: TicketState[] = [
  'Backlog',
  'Refining',
  'Implementing',
  'GitLab CI',
  'AI Review',
  'Human Review',
  'Merging',
  'Done',
  'Waiting',
  'Cancelled',
];

const RAW: Record<TicketState, { line: string; text: string }> = {
  Backlog: { line: '#a1a1aa', text: '#d4d4d8' },
  Refining: { line: '#8b5cf6', text: '#c4b5fd' },
  Implementing: { line: '#22c55e', text: '#86efac' },
  'GitLab CI': { line: '#0ea5e9', text: '#7dd3fc' },
  'AI Review': { line: '#06b6d4', text: '#7dd3fc' },
  'Human Review': { line: '#ec4899', text: '#f9a8d4' },
  // Between Human Review's pink and Done's green, because that is exactly where it sits.
  Merging: { line: '#a3e635', text: '#d9f99d' },
  Done: { line: '#16a34a', text: '#86efac' },
  Waiting: { line: '#f59e0b', text: '#fbbf24' },
  Cancelled: { line: '#ef4444', text: '#fca5a5' },
};

function alpha(hex: string, amount: number): string {
  const value = hex.replace('#', '');
  const r = Number.parseInt(value.slice(0, 2), 16);
  const g = Number.parseInt(value.slice(2, 4), 16);
  const b = Number.parseInt(value.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${amount})`;
}

export function stateStyle(state: TicketState): StateStyle {
  const raw = RAW[state];
  return {
    line: raw.line,
    text: raw.text,
    wash: alpha(raw.line, 0.1),
    edge: alpha(raw.line, 0.32),
  };
}

/**
 * Queued: parked behind other work, and waking itself when that work lands.
 *
 * Indigo rather than Waiting's amber, because amber is the colour this dashboard uses for
 * "somebody is needed" and a queued ticket needs nobody. It sits clear of Refining's violet
 * and GitLab CI's sky, the two hues it shares a board with. Mirrors `--queued` in tokens.css.
 */
const QUEUED_LINE = '#6366f1';

export const QUEUED_STYLE: StateStyle = {
  line: QUEUED_LINE,
  text: '#a5b4fc',
  wash: alpha(QUEUED_LINE, 0.1),
  edge: alpha(QUEUED_LINE, 0.32),
};

/** Stage colours are the state colours of the state each stage runs in. */
const STAGE_STATE: Record<Exclude<Stage, 'test'>, TicketState> = {
  refine: 'Refining',
  plan: 'Refining',
  implement: 'Implementing',
  review: 'AI Review',
};

/**
 * The retired Testing teal, kept for the legacy `test` runs still in real databases. It has
 * no state to borrow from any more, but a run ledger that suddenly drew old rows in a new
 * colour would read as data having changed.
 */
const LEGACY_TEST_STYLE: StateStyle = {
  line: '#14b8a6',
  text: '#6ee7d7',
  wash: 'rgba(20, 184, 166, 0.1)',
  edge: 'rgba(20, 184, 166, 0.32)',
};

export function stageStyle(stage: JobStage): StateStyle {
  if (stage === 'test') return LEGACY_TEST_STYLE;
  return stateStyle(STAGE_STATE[stage]);
}

export interface VendorStyle {
  line: string;
  text: string;
  wash: string;
  edge: string;
  label: string;
}

/**
 * Vendor liveries.
 *
 * Claude warm amber, Codex cool teal, Cursor violet — chosen to be distinguishable at desk
 * distance and, critically, to be nothing like any state colour. The adversarial pairing
 * (implementer and reviewer are never the same vendor) is the design's central claim, so which
 * vendor is doing what has to be legible without reading a word. Violet sits far enough from
 * both existing hues that a three-way board is still readable at a glance.
 */
export function vendorStyle(vendor: Vendor): VendorStyle {
  if (vendor === 'codex') {
    return {
      line: '#5eead4',
      text: '#99f6e4',
      wash: 'rgba(94, 234, 212, 0.12)',
      edge: 'rgba(94, 234, 212, 0.4)',
      label: 'codex',
    };
  }
  if (vendor === 'cursor') {
    return {
      line: '#a78bfa',
      text: '#c4b5fd',
      wash: 'rgba(167, 139, 250, 0.13)',
      edge: 'rgba(167, 139, 250, 0.42)',
      label: 'cursor',
    };
  }
  if (vendor === 'kimi') {
    return {
      line: '#fb7185',
      text: '#fda4af',
      wash: 'rgba(251, 113, 133, 0.13)',
      edge: 'rgba(251, 113, 133, 0.42)',
      label: 'kimi',
    };
  }
  return {
    line: '#d9a95c',
    text: '#f5cf8a',
    wash: 'rgba(217, 169, 92, 0.14)',
    edge: 'rgba(217, 169, 92, 0.44)',
    label: 'claude',
  };
}

/** The human-blocked states. Only these two are allowed to demand attention. */
const HUMAN_STATES: TicketState[] = ['Waiting', 'Human Review'];

export function isHumanState(state: TicketState): boolean {
  return HUMAN_STATES.includes(state);
}
